import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import MainLayout from '../../../../components/layout/MainLayout';
import { getBackofficeSidebarItems } from '../../shell/backofficeSidebar';
import { useAuth } from '../../../../context/AuthContext';
import { useCurrency } from '../../../../context/CurrencyContext';
import { X } from 'lucide-react';
import { PaginatedListLoading } from '../../../../components/ui/PaginatedListLoading';
import { formatOrderReference } from '../../../../utils/formatOrderReference';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

function ExpandableId({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return (
    <details className="max-w-[10rem] sm:max-w-[14rem]">
      <summary className="cursor-pointer list-none text-xs font-semibold text-[#00b853] hover:underline">
        {label}
        <span className="ml-1 text-slate-400 font-normal">▸</span>
      </summary>
      <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-2 font-mono text-[11px] leading-snug break-all text-slate-900">
        {value}
      </div>
    </details>
  );
}

type PaymentRow = {
  paymentKind?: 'PRIMARY' | 'BALANCE';
  id: string;
  orderId: string;
  stripePaymentIntentId: string;
  stripeChargeId: string | null;
  amountMinor: number;
  currency: string;
  stripeStatus: string;
  cardBrand: string | null;
  cardLast4: string | null;
  cardExpMonth: number | null;
  cardExpYear: number | null;
  paymentMethodType: string | null;
  receiptEmail: string | null;
  failureMessage: string | null;
  createdAt: string;
  updatedAt: string;
  order: {
    id: string;
    status: string;
    totalUsd: number;
    isPreorderOrder?: boolean;
    preorderDepositUsd?: number;
    preorderRemainingUsd?: number;
    currency: string;
    createdAt: string;
    addressSnapshot?: Record<string, unknown>;
    /** Set when the customer deleted their account; payment row is retained. */
    customerAccountDetachedAt?: string | null;
    refundRequests?: Array<{
      id: string;
      status:
        | 'REQUESTED'
        | 'APPROVED'
        | 'REJECTED'
        | 'WAITING_RETURN'
        | 'RETURN_RECEIVED'
        | 'REFUND_PROCESSING'
        | 'DONE'
        | 'FAILED';
      createdAt: string;
    }>;
  };
  customer: {
    id: string;
    email: string;
    customerProfile: {
      fullName?: string | null;
      phone?: string | null;
      country?: string | null;
      city?: string | null;
    } | null;
  } | null;
  refunds?: Array<{ id: string; amountMinor: number; status: string }>;
};

function isPaymentCustomerAccountDeleted(r: PaymentRow): boolean {
  return Boolean(r.order.customerAccountDetachedAt);
}

function paymentDisplayEmail(r: PaymentRow): string {
  return (
    r.customer?.email?.trim() ||
    r.receiptEmail?.trim() ||
    (typeof r.order.addressSnapshot === 'object' &&
    r.order.addressSnapshot &&
    typeof (r.order.addressSnapshot as { email?: unknown }).email === 'string'
      ? String((r.order.addressSnapshot as { email: string }).email).trim()
      : '') ||
    '—'
  );
}

function paymentDisplayName(r: PaymentRow): string {
  const fromProfile = r.customer?.customerProfile?.fullName?.trim();
  if (fromProfile) return fromProfile;
  const snap = r.order.addressSnapshot;
  if (snap && typeof snap === 'object' && typeof (snap as { fullName?: unknown }).fullName === 'string') {
    const n = String((snap as { fullName: string }).fullName).trim();
    if (n) return n;
  }
  return '—';
}

function PaymentOrderBalanceSummary({
  row,
  formatPrice,
  t,
}: {
  row: PaymentRow;
  formatPrice: (usd: number) => string;
  t: (key: string) => string;
}) {
  const totalUsd = Number(row.order.totalUsd ?? 0);
  const isPre = Boolean(row.order.isPreorderOrder);
  const deposit = Number(row.order.preorderDepositUsd ?? 0);
  const remaining = Number(row.order.preorderRemainingUsd ?? 0);
  return (
    <div className="min-w-[7.5rem] text-xs leading-snug text-slate-700">
      <div>
        <span className="text-slate-500">{t('backoffice.payments_order_total_short')}</span>{' '}
        <span className="font-medium text-slate-900">{formatPrice(totalUsd)}</span>
      </div>
      {isPre && deposit > 0 ? (
        <div className="mt-0.5">
          <span className="text-slate-500">{t('backoffice.payments_order_deposit_short')}</span>{' '}
          <span className="font-medium text-slate-800">{formatPrice(deposit)}</span>
        </div>
      ) : null}
      {isPre && remaining > 0 ? (
        <div className="mt-0.5 font-semibold text-amber-800">
          <span className="font-normal text-amber-900/85">
            {t('backoffice.payments_order_remaining_short')}:{' '}
          </span>
          {formatPrice(remaining)}
        </div>
      ) : null}
    </div>
  );
}

export default function WorkspacePaymentsPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const { authorizedFetch } = useAuth();
  const { currency, formatPrice, formatPriceFrom, formatMajorInCurrency, rates } = useCurrency();
  const [rows, setRows] = useState<PaymentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailData, setDetailData] = useState<Record<string, unknown> | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [refundModal, setRefundModal] = useState<{
    paymentId: string;
    maxMinor: number;
    currency: string;
    amountMinor: string;
  } | null>(null);
  const globalAuthorizedFetch = useCallback(
    (input: RequestInfo | URL, init: RequestInit = {}) => {
      const headers = new Headers(init.headers || {});
      // Payments workspace is intentionally global (not department-scoped).
      headers.set('x-department-slug', '');
      return authorizedFetch(input, { ...init, headers });
    },
    [authorizedFetch],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = new URL(`${API_BASE}/payments`);
      if (q.trim()) url.searchParams.set('q', q.trim());
      if (status) url.searchParams.set('status', status);
      if (from) url.searchParams.set('from', `${from}T00:00:00.000Z`);
      if (to) url.searchParams.set('to', `${to}T23:59:59.999Z`);
      const res = await globalAuthorizedFetch(url.toString());
      if (!res.ok) throw new Error('load');
      setRows((await res.json()) as PaymentRow[]);
    } catch {
      setError(t('backoffice.payments_load_error'));
    } finally {
      setLoading(false);
    }
  }, [from, globalAuthorizedFetch, q, status, t, to]);

  useEffect(() => {
    void load();
  }, [load]);

  const openDetail = async (id: string) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailData(null);
    try {
      const res = await globalAuthorizedFetch(`${API_BASE}/payments/${id}`);
      if (!res.ok) throw new Error('detail');
      setDetailData((await res.json()) as Record<string, unknown>);
    } catch {
      setDetailData(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setDetailData(null);
  };

  const submitRefund = async () => {
    if (!refundModal) return;
    try {
      const amountMinor = Number(refundModal.amountMinor || 0);
      if (!Number.isFinite(amountMinor) || amountMinor <= 0) {
        setError('Refund amount must be greater than 0.');
        return;
      }
      const res = await globalAuthorizedFetch(
        `${API_BASE}/payments/${refundModal.paymentId}/refund`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ amountMinor }),
        },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as any).message || 'Unable to process refund');
      }
      setRefundModal(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unable to process refund');
    }
  };

  const amountDisplay = (r: PaymentRow) => {
    const major = r.amountMinor / 100;
    return formatPriceFrom(major, (r.currency || 'USD').toUpperCase());
  };

  const toWorkspaceCurrency = useCallback(
    (amount: number, fromCurrency: string): number | null => {
      const from = (fromCurrency || 'USD').toUpperCase();
      const toCur = (currency || 'USD').toUpperCase();
      const fromRate = from === 'USD' ? 1 : Number(rates[from] ?? 0);
      const toRate = toCur === 'USD' ? 1 : Number(rates[toCur] ?? 0);
      if (!Number.isFinite(fromRate) || fromRate <= 0) return null;
      if (!Number.isFinite(toRate) || toRate <= 0) return null;
      const usdAmount = amount / fromRate;
      return Number((usdAmount * toRate).toFixed(2));
    },
    [currency, rates],
  );

  const toUsd = useCallback(
    (amount: number, fromCurrency: string): number | null => {
      const from = (fromCurrency || 'USD').toUpperCase();
      const fromRate = from === 'USD' ? 1 : Number(rates[from] ?? 0);
      if (!Number.isFinite(fromRate) || fromRate <= 0) return null;
      return Number((amount / fromRate).toFixed(6));
    },
    [rates],
  );

  const expectedOrderDueUsd = useCallback((r: PaymentRow): number => {
    const total = Number(r.order.totalUsd || 0);
    if (!r.order.isPreorderOrder) return total;
    const deposit = Number(r.order.preorderDepositUsd ?? 0);
    const remaining = Number(r.order.preorderRemainingUsd ?? 0);
    const paidUsd = toUsd(r.amountMinor / 100, r.currency || 'USD');
    if (paidUsd == null) {
      const status = String(r.order.status || '');
      if (status === 'PREORDER_AWAITING_RELEASE') return deposit > 0 ? deposit : total;
      if (status === 'PREORDER_BALANCE_DUE') return remaining > 0 ? remaining : total;
      return total;
    }
    const candidates = [total, deposit, remaining].filter((v) => Number.isFinite(v) && v > 0);
    if (!candidates.length) return total;
    return candidates.reduce((best, cur) =>
      Math.abs(cur - paidUsd) < Math.abs(best - paidUsd) ? cur : best,
    );
  }, [toUsd]);

  const fxDeltaDisplay = useCallback(
    (r: PaymentRow): { text: string; positive: boolean; negative: boolean } => {
      const paidMajor = r.amountMinor / 100;
      const paidConverted = toWorkspaceCurrency(paidMajor, r.currency || 'USD');
      const expectedConverted = toWorkspaceCurrency(expectedOrderDueUsd(r), 'USD');
      if (paidConverted == null || expectedConverted == null) {
        return { text: '...', positive: false, negative: false };
      }
      const delta = Number((paidConverted - expectedConverted).toFixed(2));
      const sign = delta > 0 ? '+' : delta < 0 ? '-' : '';
      return {
        text: `${sign}${formatPriceFrom(Math.abs(delta), (currency || 'USD').toUpperCase())}`,
        positive: delta > 0,
        negative: delta < 0,
      };
    },
    [currency, expectedOrderDueUsd, formatPriceFrom, toWorkspaceCurrency],
  );

  return (
    <MainLayout
      withSidebar
      sidebarItems={getBackofficeSidebarItems(t)}
      activePath={`${location.pathname}${location.search}`}
    >
      <main className="workspace-page">
        <section>
          <h1 className="workspace-page-title">
            {t('backoffice.payments_title')}
          </h1>
          <p className="workspace-page-subtitle text-slate-600">
            {t('backoffice.payments_subtitle')}
          </p>
        </section>
        <div className="space-y-4">
        <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-slate-200 bg-white p-3 sm:p-4 shadow-sm">
          <div className="w-full min-w-0 flex-1 sm:min-w-[160px]">
            <label className="mb-1 block text-xs font-medium text-slate-600">
              {t('backoffice.payments_filter_search')}
            </label>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              placeholder={t('backoffice.payments_filter_search_ph')}
            />
          </div>
          <div className="w-full sm:w-auto">
            <label className="mb-1 block text-xs font-medium text-slate-600">
              {t('backoffice.payments_filter_status')}
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm sm:w-auto"
            >
              <option value="">{t('common.all')}</option>
              <option value="succeeded">succeeded</option>
              <option value="requires_payment_method">requires_payment_method</option>
              <option value="processing">processing</option>
              <option value="canceled">canceled</option>
            </select>
          </div>
          <div className="w-full sm:w-auto">
            <label className="mb-1 block text-xs font-medium text-slate-600">
              {t('backoffice.payments_filter_from')}
            </label>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm sm:w-auto"
            />
          </div>
          <div className="w-full sm:w-auto">
            <label className="mb-1 block text-xs font-medium text-slate-600">
              {t('backoffice.payments_filter_to')}
            </label>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm sm:w-auto"
            />
          </div>
          <button
            type="button"
            onClick={() => void load()}
            className="w-full rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 sm:w-auto"
          >
            {t('backoffice.payments_apply')}
          </button>
        </div>

        {error ? (
          <div className="rounded-none border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        <div className="space-y-3 md:hidden">
          {loading ? (
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-sm text-slate-500">{t('common.loading')}</p>
            </div>
          ) : rows.length === 0 ? (
            <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-500 shadow-sm">
              {t('backoffice.payments_empty')}
            </div>
          ) : (
            rows.map((r) => {
              const accountDeleted = isPaymentCustomerAccountDeleted(r);
              const latestRefundRequest = r.order.refundRequests?.[0];
              const refundButtonEnabled =
                r.paymentKind !== 'BALANCE'
                && r.stripeStatus === 'succeeded'
                && r.order.status !== 'REFUNDED'
                && !!latestRefundRequest
                && ['APPROVED', 'RETURN_RECEIVED'].includes(
                  latestRefundRequest.status,
                );
              const delta = fxDeltaDisplay(r);
              return (
                <article
                  key={`mobile-${r.id}`}
                  className={[
                    'rounded-2xl border bg-white p-4 shadow-sm',
                    accountDeleted ? 'border-amber-200 bg-amber-50/40' : 'border-slate-200',
                  ].join(' ')}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-xs text-slate-500">{new Date(r.createdAt).toLocaleString()}</p>
                      <p className="mt-0.5 text-sm font-semibold text-slate-900">{paymentDisplayEmail(r)}</p>
                      <p className="text-xs text-slate-500">{paymentDisplayName(r)}</p>
                    </div>
                    <span
                      className={[
                        'rounded-full px-2 py-0.5 text-[11px] font-semibold',
                        r.stripeStatus === 'succeeded'
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-slate-100 text-slate-700',
                      ].join(' ')}
                    >
                      {r.stripeStatus}
                    </span>
                  </div>
                  <div className="mt-3 rounded-lg border border-slate-100 bg-slate-50/80 p-2">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                      {t('backoffice.payments_col_order_balance')}
                    </p>
                    <div className="mt-1">
                      <PaymentOrderBalanceSummary row={r} formatPrice={formatPrice} t={t} />
                    </div>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <p className="text-slate-500" title={t('backoffice.payments_col_amount_tooltip')}>
                        {t('backoffice.payments_col_amount')}
                      </p>
                      <p className="font-semibold text-slate-900">{amountDisplay(r)}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Customer paid</p>
                      <p className="font-semibold text-slate-900">
                        {formatMajorInCurrency(r.amountMinor / 100, (r.currency || 'USD').toUpperCase())}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-500">FX gain/loss</p>
                      <p
                        className={[
                          'font-semibold',
                          delta.positive ? 'text-emerald-700' : '',
                          delta.negative ? 'text-rose-700' : '',
                          !delta.positive && !delta.negative ? 'text-slate-500' : '',
                        ].join(' ')}
                      >
                        {delta.text}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-500">{t('backoffice.payments_col_card')}</p>
                      <p className="font-medium text-slate-700">
                        {r.cardBrand && r.cardLast4 ? `${r.cardBrand} ····${r.cardLast4}` : '—'}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => void openDetail(r.id)}
                      className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-800"
                    >
                      {t('backoffice.payments_view')}
                    </button>
                    {r.paymentKind !== 'BALANCE' && r.stripeStatus === 'succeeded' && r.order.status !== 'REFUNDED' ? (
                      <button
                        type="button"
                        disabled={!refundButtonEnabled}
                        onClick={() =>
                          setRefundModal({
                            paymentId: r.id,
                            maxMinor: r.amountMinor,
                            currency: r.currency || 'USD',
                            amountMinor: String(r.amountMinor),
                          })
                        }
                        className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400"
                      >
                        Refund
                      </button>
                    ) : null}
                  </div>
                </article>
              );
            })
          )}
        </div>

        <div className="hidden overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm md:block">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-600">
              <tr>
                <th className="px-4 py-3">{t('backoffice.payments_col_date')}</th>
                <th className="px-4 py-3">{t('backoffice.payments_col_customer')}</th>
                <th className="px-4 py-3">{t('backoffice.payments_col_order_id')}</th>
                <th className="px-4 py-3">{t('backoffice.payments_col_payment_id')}</th>
                <th className="px-4 py-3">{t('backoffice.payments_col_order_balance')}</th>
                <th
                  className="px-4 py-3"
                  title={t('backoffice.payments_col_amount_tooltip')}
                >
                  {t('backoffice.payments_col_amount')}
                </th>
                <th className="px-4 py-3">Customer paid</th>
                <th className="px-4 py-3">FX gain/loss</th>
                <th className="px-4 py-3">{t('backoffice.payments_col_status')}</th>
                <th className="px-4 py-3">{t('backoffice.payments_col_card')}</th>
                <th className="px-4 py-3">{t('backoffice.payments_col_type')}</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <PaginatedListLoading tableColSpan={12} title={t('common.list_loading_title')} />
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={12} className="px-4 py-8 text-center text-slate-500">
                    {t('backoffice.payments_empty')}
                  </td>
                </tr>
              ) : (
                rows.map((r) => {
                  const accountDeleted = isPaymentCustomerAccountDeleted(r);
                  const latestRefundRequest = r.order.refundRequests?.[0];
                  const refundButtonEnabled =
                    r.paymentKind !== 'BALANCE'
                    && r.stripeStatus === 'succeeded'
                    && r.order.status !== 'REFUNDED'
                    && !!latestRefundRequest
                    && ['APPROVED', 'RETURN_RECEIVED'].includes(
                      latestRefundRequest.status,
                    );
                  return (
                  <tr
                    key={r.id}
                    className={[
                      'border-b hover:bg-slate-50/80',
                      accountDeleted ? 'border-amber-100 bg-amber-50/40' : 'border-slate-100',
                    ].join(' ')}
                  >
                    <td className="whitespace-nowrap px-4 py-3 text-slate-700">
                      {new Date(r.createdAt).toLocaleString()}
                    </td>
                    <td className="px-4 py-3">
                      {accountDeleted ? (
                        <div className="mb-1.5">
                          <span className="inline-flex items-center rounded-full border border-amber-300 bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-950">
                            {t('backoffice.payments_customer_deleted_badge')}
                          </span>
                        </div>
                      ) : null}
                      <div className="font-medium text-slate-900">{paymentDisplayEmail(r)}</div>
                      <div className="text-xs text-slate-500">{paymentDisplayName(r)}</div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <ExpandableId
                        label={t('backoffice.payments_expand_order_id')}
                        value={formatOrderReference(r.orderId)}
                      />
                    </td>
                    <td className="px-4 py-3 align-top">
                      <ExpandableId
                        label={t('backoffice.payments_expand_payment_id')}
                        value={r.id}
                      />
                    </td>
                    <td className="px-4 py-3 align-top">
                      <PaymentOrderBalanceSummary row={r} formatPrice={formatPrice} t={t} />
                    </td>
                    <td className="px-4 py-3">{amountDisplay(r)}</td>
                    <td className="px-4 py-3 text-slate-700">
                      {formatMajorInCurrency(r.amountMinor / 100, (r.currency || 'USD').toUpperCase())}
                    </td>
                    <td className="px-4 py-3">
                      {(() => {
                        const delta = fxDeltaDisplay(r);
                        return (
                          <span
                            className={[
                              'font-semibold',
                              delta.positive ? 'text-emerald-700' : '',
                              delta.negative ? 'text-rose-700' : '',
                              !delta.positive && !delta.negative ? 'text-slate-500' : '',
                            ].join(' ')}
                          >
                            {delta.text}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={[
                          'rounded-full px-2 py-0.5 text-xs font-semibold',
                          r.stripeStatus === 'succeeded'
                            ? 'bg-emerald-100 text-emerald-800'
                            : 'bg-slate-100 text-slate-700',
                        ].join(' ')}
                      >
                        {r.stripeStatus}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      {r.cardBrand && r.cardLast4
                        ? `${r.cardBrand} ····${r.cardLast4}`
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{r.paymentMethodType || '—'}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-3">
                        <button
                          type="button"
                          onClick={() => void openDetail(r.id)}
                          className="text-sm font-semibold text-[#00b853] hover:underline"
                        >
                          {t('backoffice.payments_view')}
                        </button>
                        {r.paymentKind !== 'BALANCE' && r.stripeStatus === 'succeeded' && r.order.status !== 'REFUNDED' ? (
                          <button
                            type="button"
                            disabled={!refundButtonEnabled}
                            onClick={() =>
                              setRefundModal({
                                paymentId: r.id,
                                maxMinor: r.amountMinor,
                                currency: r.currency || 'USD',
                                amountMinor: String(r.amountMinor),
                              })
                            }
                            className="text-sm font-semibold text-amber-700 hover:underline disabled:cursor-not-allowed disabled:text-slate-400 disabled:no-underline"
                            title={
                              refundButtonEnabled
                                ? 'Refund request validated: payment refund enabled.'
                                : 'Refund becomes available after admin approval (or return received for delivered requests).'
                            }
                          >
                            Refund
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
                })
              )}
            </tbody>
          </table>
        </div>

        <p className="text-xs text-slate-500">
          {t('backoffice.payments_pci_note')}
        </p>
        </div>

      {detailOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between gap-2">
              <h2 className="text-base font-semibold text-slate-900">
                {t('backoffice.payments_detail_title')}
              </h2>
              <button
                type="button"
                onClick={closeDetail}
                className="rounded-full p-2 text-slate-500 hover:bg-slate-100"
                aria-label={t('common.cancel')}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            {detailLoading ? (
              <p className="text-sm text-slate-600">{t('common.loading')}</p>
            ) : detailData ? (
              <>
                {(() => {
                  const order = detailData.order as
                    | { customerAccountDetachedAt?: string | null }
                    | undefined;
                  if (!order?.customerAccountDetachedAt) return null;
                  return (
                    <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950">
                      <span className="font-semibold">
                        {t('backoffice.payments_customer_deleted_badge')}
                      </span>
                      {' — '}
                      {t('backoffice.payments_customer_deleted_hint')}
                    </div>
                  );
                })()}
                <pre className="max-h-[60vh] overflow-auto rounded-xl bg-slate-900 p-4 text-xs text-slate-100">
                  {JSON.stringify(detailData, null, 2)}
                </pre>
              </>
            ) : (
              <p className="text-sm text-red-600">{t('backoffice.payments_detail_error')}</p>
            )}
          </div>
        </div>
      ) : null}
      {refundModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl">
            <h3 className="text-base font-semibold text-slate-900">Refund payment</h3>
            <p className="mt-1 text-xs text-slate-500">
              Enter amount in minor units ({refundModal.currency}). Max {refundModal.maxMinor}.
            </p>
            <input
              type="number"
              min={1}
              max={refundModal.maxMinor}
              value={refundModal.amountMinor}
              onChange={(e) =>
                setRefundModal((prev) =>
                  prev ? { ...prev, amountMinor: e.target.value } : prev,
                )
              }
              className="mt-3 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setRefundModal(null)}
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void submitRefund()}
                className="rounded-lg bg-amber-600 px-3 py-2 text-sm font-semibold text-white"
              >
                Process refund
              </button>
            </div>
          </div>
        </div>
      ) : null}
      </main>
    </MainLayout>
  );
}
